import { ORIENTATIONS, type Orientation } from '../types'
import type { TimelineAsset, TimelineItem } from './types'

/**
 * Orientation and refit helpers (#77): the three canvases, and how an
 * edited document follows a switch between them. Pure, shared by the editor
 * (the orientation control) and the server (the converter).
 */

export type Size = { width: number; height: number }

export function orientationOf({ width, height }: Size): Orientation {
  return height > width ? 'portrait' : height < width ? 'landscape' : 'square'
}

/** The canvas of an orientation; every side is even, so H.264 accepts it. */
export function sizeOf(orientation: Orientation): Size {
  return { ...ORIENTATIONS[orientation] }
}

/** A contain-fit box for a picture on the canvas, centred. */
export function fitBox(
  asset: { width?: number | null; height?: number | null },
  canvas: Size,
): { width: number; height: number; left: number; top: number } {
  const w = asset.width || canvas.width
  const h = asset.height || canvas.height
  const scale = Math.min(canvas.width / w, canvas.height / h)
  const width = Math.round(w * scale)
  const height = Math.round(h * scale)
  return {
    width,
    height,
    left: Math.round((canvas.width - width) / 2),
    top: Math.round((canvas.height - height) / 2),
  }
}

/**
 * Every item re-fitted from one canvas to another: boxes scale by the axis
 * ratios, a text's font size, a border radius and pixel crops by the width
 * ratio; a scene (recording video or audio) stays at the full canvas, where
 * `objectFit: cover` handles the shape; a picture or an intro/outro video is
 * contain-fitted again from its asset's dimensions. Captions keep their
 * typography: a template's captions are re-typeset by the re-apply.
 */
export function refitItems(
  items: Record<string, TimelineItem>,
  assets: Record<string, TimelineAsset>,
  from: Size,
  to: Size,
): Record<string, TimelineItem> {
  const sx = to.width / from.width
  const sy = to.height / from.height
  const out: Record<string, TimelineItem> = {}
  for (const [id, item] of Object.entries(items)) {
    out[id] = refitItem(item, assets, { sx, sy }, to)
  }
  return out
}

function refitItem(
  item: TimelineItem,
  assets: Record<string, TimelineAsset>,
  { sx, sy }: { sx: number; sy: number },
  to: Size,
): TimelineItem {
  const scaled = {
    top: item.top * sy,
    left: item.left * sx,
    width: item.width * sx,
    height: item.height * sy,
  }
  const canvas = { top: 0, left: 0, width: to.width, height: to.height }
  switch (item.type) {
    case 'audio':
    case 'scene':
      return { ...item, ...canvas }
    case 'view':
      return {
        ...item,
        ...scaled,
        ...cropsOf(item, sx),
        borderRadius: item.borderRadius * sx,
      }
    case 'video': {
      if (item.zealous.source.kind === 'recording') {
        return { ...item, ...canvas, ...cropsOf(item, sx) }
      }
      const asset = assets[item.assetId]
      const box = asset && asset.type === 'video' ? fitBox(asset, to) : scaled
      return {
        ...item,
        ...box,
        ...cropsOf(item, sx),
        borderRadius: item.borderRadius * sx,
      }
    }
    case 'image': {
      const asset = assets[item.assetId]
      const box = asset && asset.type === 'image' ? fitBox(asset, to) : scaled
      return {
        ...item,
        ...box,
        ...cropsOf(item, sx),
        borderRadius: item.borderRadius * sx,
      }
    }
    case 'text':
      return {
        ...item,
        ...scaled,
        fontSize: item.fontSize * sx,
        background: item.background && {
          ...item.background,
          horizontalPadding: item.background.horizontalPadding * sx,
          borderRadius: item.background.borderRadius * sx,
        },
      }
    case 'solid':
      return {
        ...item,
        ...scaled,
        borderRadius: item.borderRadius * sx,
        ...(item.stroke
          ? { stroke: { ...item.stroke, width: item.stroke.width * sx } }
          : {}),
      }
    case 'captions':
    case 'component':
      return { ...item, ...scaled }
  }
}

function cropsOf(
  item: {
    cropLeft: number
    cropTop: number
    cropRight: number
    cropBottom: number
  },
  sx: number,
) {
  return {
    cropLeft: item.cropLeft * sx,
    cropTop: item.cropTop * sx,
    cropRight: item.cropRight * sx,
    cropBottom: item.cropBottom * sx,
  }
}
